<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">
            <!-- Sidebar -->
            <AppSidebar :activeItem="sidebarActive" />

            <!-- Main Card -->
            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="cl-wrapper">

                    <!-- Header -->
                    <div class="cl-header">
                        <div class="cl-header-left">
                            <div class="cl-header-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M9 11l3 3L22 4" />
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                </svg>
                            </div>
                            <div>
                                <h1 class="cl-title">Features Checklist</h1>
                                <p class="cl-subtitle">Track your pre-bid documentation &amp; add remarks for each item.</p>
                            </div>
                        </div>
                        <div class="cl-progress" v-if="!loading">
                            <div class="cl-progress-ring">
                                <span class="cl-progress-num">{{ completedCount }}/{{ totalCount }}</span>
                            </div>
                            <div class="cl-progress-bar-wrap">
                                <div class="cl-progress-bar" :style="{ width: progressPercent + '%' }"></div>
                            </div>
                            <span class="cl-progress-label">{{ progressPercent }}% complete</span>
                        </div>
                    </div>

                    <!-- Loading -->
                    <div v-if="loading" class="cl-loading">
                        <div class="cl-spinner"></div>
                        <p>Loading checklist…</p>
                    </div>

                    <!-- Load error -->
                    <div v-else-if="loadError" class="cl-empty is-error" role="alert">
                        {{ loadError }}
                        <button type="button" class="cl-retry" @click="fetchChecklist">Try Again</button>
                    </div>

                    <!-- Empty -->
                    <div v-else-if="!checkItems.length" class="cl-empty">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                        No checklist items for this bid yet. They appear once the analysis finishes.
                    </div>

                    <!-- Checklist Table -->
                    <template v-else>
                        <div class="cl-table">
                            <div class="cl-table-head">
                                <span class="col-check">Done</span>
                                <span class="col-task">Task</span>
                                <span class="col-remarks">Remarks</span>
                                <span class="col-updated">Last Updated</span>
                                <span class="col-action"></span>
                            </div>

                            <div v-for="item in checkItems" :key="item.id" class="cl-row"
                                :class="{ 'is-done': item.completed }">
                                <!-- Checkbox -->
                                <div class="col-check">
                                    <label class="cl-checkbox">
                                        <input type="checkbox" :checked="!!item.completed"
                                            :disabled="$isViewOnly()" :aria-label="'Done: ' + item.task"
                                            @change="toggleCompleted(item, $event)" />
                                        <span class="cl-checkmark">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="3.5" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </span>
                                    </label>
                                </div>

                                <!-- Task -->
                                <div class="col-task">
                                    <span class="cl-task-id">#{{ item.id }}</span>
                                    <span class="cl-task-text">{{ item.task }}</span>
                                </div>

                                <!-- Remarks -->
                                <div class="col-remarks">
                                    <textarea class="cl-remarks-input" rows="2" placeholder="Add a remark…"
                                        :aria-label="'Remarks for ' + item.task"
                                        v-model="item.remarks" :disabled="$isViewOnly()"></textarea>
                                </div>

                                <!-- Last updated -->
                                <div class="col-updated">
                                    <span v-if="item.update_date" class="cl-date">{{ formatDate(item.update_date)
                                        }}</span>
                                    <span v-else class="cl-date muted">—</span>
                                </div>

                                <!-- Save action -->
                                <div class="col-action">
                                    <button class="cl-save-btn admin-only" :disabled="savingId === item.id"
                                        @click="saveItem(item)">
                                        <svg v-if="savingId !== item.id" width="15" height="15" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" stroke-width="2">
                                            <path
                                                d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                            <polyline points="17 21 17 13 7 13 7 21" />
                                            <polyline points="7 3 7 8 15 8" />
                                        </svg>
                                        <svg v-else class="cl-spin" width="15" height="15" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" stroke-width="2.5">
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                        {{ savingId === item.id ? 'Saving…' : 'Save' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Footer -->
                    <div v-if="!loading" class="cl-footer">
                        <button class="cl-back" @click="goBack">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.5" aria-hidden="true">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                            Back
                        </button>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import { notify } from '@/utils/notify';

export default {
    name: 'BidChecklist',
    components: { AppSidebar },
    props: {
        getAccessToken: Function,
    },
    data() {
        return {
            loading: true,
            savingId: null,
            checkItems: [],
            loadError: '',
        };
    },
    computed: {
        pdfId() {
            return this.$route.params.pdf_id;
        },
        // Keep the sidebar on whatever section we were opened from; default to the
        // pre-bid "Bids" section when opened directly.
        sidebarActive() {
            const from = String(this.$route.query.from || '').trim();
            const allowed = ['tenders', 'bookmarks', 'files', 'upload', 'active', 'completed', 'knowledge', 'admin', 'dashboard'];
            return allowed.includes(from) ? from : 'files';
        },
        totalCount() {
            return this.checkItems.length;
        },
        completedCount() {
            return this.checkItems.filter(i => !!i.completed).length;
        },
        progressPercent() {
            if (!this.totalCount) return 0;
            return Math.round((this.completedCount / this.totalCount) * 100);
        },
    },
    mounted() {
        this.fetchChecklist();
    },
    methods: {
        authHeaders() {
            // getAccessToken prop when provided, otherwise read user-info like other pages.
            let token = '';
            if (typeof this.getAccessToken === 'function') {
                token = this.getAccessToken();
            } else {
                try {
                    const info = JSON.parse(localStorage.getItem('user-info'));
                    token = info && (info.access_token || info.token);
                } catch (e) { /* ignore */ }
            }
            return { Authorization: 'Bearer ' + token };
        },
        async fetchChecklist() {
            if (!this.pdfId) { this.loading = false; return; }
            this.loading = true;
            this.loadError = '';
            try {
                const res = await axios.get(
                    this.$API_URL + '/user/pdf-checklist?pdf_id=' + this.pdfId,
                    { headers: this.authHeaders() }
                );
                const data = res.data || {};
                this.checkItems = Array.isArray(data.check_items) ? data.check_items : [];
            } catch (err) {
                console.error('Failed to load checklist', err);
                this.loadError = 'Couldn’t load the checklist. Check your connection and try again.';
            } finally {
                this.loading = false;
            }
        },
        toggleCompleted(item, event) {
            // Update local state immediately; persist on Save.
            item.completed = event.target.checked ? 1 : 0;
        },
        async saveItem(item) {
            if (this.$isViewOnly()) return;   // viewers cannot save checklist updates
            if (this.savingId !== null) return;
            this.savingId = item.id;
            try {
                const res = await axios.post(
                    this.$API_URL + '/user/update-checklist-item',
                    {
                        pdf_id: Number(this.pdfId),
                        id: item.id,
                        completed: item.completed ? 1 : 0,
                        remarks: item.remarks || '',
                    },
                    { headers: this.authHeaders() }
                );
                // Server stamps update_date for this respective item only — reflect it.
                const updated = res.data && res.data.item;
                if (updated && updated.update_date) {
                    item.update_date = updated.update_date;
                }
                notify.success('Checklist item saved');
            } catch (err) {
                console.error('Failed to save checklist item', err);
                notify.error('Couldn’t save the checklist item. Try again.');
            } finally {
                this.savingId = null;
            }
        },
        formatDate(d) {
            if (!d) return '—';
            const dt = new Date(d.replace(' ', 'T') + 'Z');
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        },
        goBack() {
            const back = (window.history.state && window.history.state.back) || '';
            if (back.split('?')[0] === `/bid-details/${this.pdfId}`) {
                this.$router.back();
                return;
            }
            // The Checklist card lives in the Analysis tab.
            const query = { tab: 'analysis' };
            const status = String(this.$route.query.status || '').trim().toUpperCase();
            if (status) query.status = status;
            else if (this.sidebarActive === 'completed') query.status = 'COMPLETED';
            else if (this.sidebarActive === 'active') query.status = 'WIN';
            this.$router.push({ name: 'BidDetails', params: { pdf_id: this.pdfId }, query });
        },
    },
};
</script>

<style scoped>
.cl-wrapper {
    width: 100%;
    position: relative;
    z-index: 2;
    font-family: var(--font-sans);
}

/* ===== Header ===== */
.cl-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    background: linear-gradient(135deg, #001F3F 0%, #003D7A 60%, #001F3F 100%);
    color: #fff;
    border-radius: 20px;
    padding: 1.6rem 2rem;
    margin-bottom: 1.75rem;
    position: relative;
    overflow: hidden;
}

.cl-header::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.4;
    pointer-events: none;
}

.cl-header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    z-index: 2;
}

.cl-header-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.16);
    backdrop-filter: blur(4px);
    flex-shrink: 0;
}

.cl-title {
    font-family: var(--font-display);   /* Outfit — page title */
    margin: 0;
    font-size: var(--fs-h1);            /* 36px page title */
    line-height: var(--lh-h1);          /* 44px */
    font-weight: var(--fw-h1);          /* 700 */
    letter-spacing: -0.02em;
    /* White on the navy header — matches the BidDetails hero title. Needed to
       beat the global `h1 { color: var(--text-primary) }` (dark ink) rule. */
    color: #fff !important;
}

.cl-subtitle {
    margin: 0.3rem 0 0;
    font-size: var(--fs-sm);            /* 13px small */
    line-height: var(--lh-sm);          /* 20px */
    font-weight: var(--fw-sm);          /* 500 */
    /* White (dimmed) on the navy header — matches BidDetails' .bd-subname. */
    color: rgba(255, 255, 255, 0.8);
}

.cl-progress {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
    min-width: 180px;
}

.cl-progress-ring {
    background: rgba(255, 255, 255, 0.18);
    padding: 0.4rem 1rem;
    border-radius: 20px;
}

.cl-progress-num {
    font-size: 0.95rem;
    font-weight: 700;
}

.cl-progress-bar-wrap {
    width: 100%;
    height: 7px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    overflow: hidden;
}

.cl-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #4ade80, #10b981);
    border-radius: 10px;
    transition: width 0.4s ease;
}

.cl-progress-label {
    font-size: 0.72rem;
    opacity: 0.8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 700;
}

/* ===== Loading / Empty ===== */
.cl-loading,
.cl-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 4rem 0;
    color: #64748b;
    font-weight: 600;
}

.cl-empty {
    flex-direction: row;
    gap: 0.5rem;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    color: #64748b;
}

.cl-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top-color: #0071C5;
    border-radius: 50%;
    animation: cl-spin 1s linear infinite;
}

/* ===== Table ===== */
.cl-table {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.cl-table-head,
.cl-row {
    display: grid;
    grid-template-columns: 60px 1.6fr 1.4fr 150px 110px;
    gap: 1rem;
    align-items: center;
}

.cl-table-head {
    padding: 0.5rem 1.1rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
}

.cl-row {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 1rem 1.1rem;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
    transition: all 0.2s;
}

.cl-row:hover {
    border-color: rgba(0, 132, 255, 0.25);
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.07);
}

.cl-row.is-done {
    background: #f0fdf4;
    border-color: rgba(16, 185, 129, 0.3);
}

/* Checkbox */
.cl-checkbox {
    position: relative;
    display: inline-flex;
    cursor: pointer;
}

.cl-checkbox input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.cl-checkmark {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    border: 2px solid #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: all 0.2s;
    background: #fff;
}

.cl-checkmark svg {
    opacity: 0;
    transform: scale(0.5);
    transition: all 0.2s;
}

.cl-checkbox input:checked+.cl-checkmark {
    background: #047857;
    border-color: #047857;
}

.cl-checkbox input:checked+.cl-checkmark svg {
    opacity: 1;
    transform: scale(1);
}

/* Task */
.col-task {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
}

.cl-task-id {
    font-size: 0.72rem;
    font-weight: 700;
    color: #0071C5;
    background: rgba(0, 113, 197, 0.1);
    padding: 2px 8px;
    border-radius: 20px;
    flex-shrink: 0;
    margin-top: 0.1rem;
}

.cl-task-text {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.45;
    text-align: left;
}

/* Remarks */
.cl-remarks-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 0.5rem 0.7rem;
    font-size: 0.82rem;
    font-family: var(--font-sans);
    color: #334155;
    resize: vertical;
    background: #f8fafc;
    transition: all 0.2s;
}

.cl-remarks-input:focus {
    outline: none;
    border-color: #0071C5;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.1);
}

/* Date */
.cl-date {
    font-size: 0.78rem;
    font-weight: 600;
    color: #475569;
}

.cl-date.muted {
    color: #64748b;
}

.cl-empty.is-error {
    flex-wrap: wrap;
    color: #b42318;
    border-color: #fecdca;
    background: #fef3f2;
}

.cl-retry {
    padding: 0.45rem 0.9rem;
    border-radius: 10px;
    border: 1px solid #b42318;
    background: #fff;
    color: #b42318;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
}

/* Save */
.col-action {
    display: flex;
    justify-content: flex-end;
}

.cl-save-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.55rem 0.9rem;
    border: none;
    border-radius: 10px;
    background: #0071C5;
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 113, 197, 0.25);
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.cl-save-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.05);
}

.cl-save-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.cl-spin {
    animation: cl-spin 1s linear infinite;
}

/* Footer */
.cl-footer {
    display: flex;
    justify-content: flex-start;
    margin-top: 1.75rem;
    padding-top: 1.25rem;
    border-top: 1px dashed #eef2f7;
}

.cl-back {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 0.55rem 1rem;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.cl-back:hover {
    background: #f1f5f9;
    transform: translateX(-3px);
}

@keyframes cl-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Responsive — stack columns on narrow screens */
@media (max-width: 900px) {

    .cl-table-head {
        display: none;
    }

    .cl-row {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }

    .col-action {
        justify-content: flex-start;
    }
}
</style>
